import { FC, useCallback, useContext, useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client';

import { AuthContext } from 'auth';

import { DEFAULT_STATE, FeaturesContext } from './constants';
import { IFeaturesState, IGetFeaturesResponse } from './interfaces';
import { GET_FEATURES } from './schema';

export const Features: FC = ({ children }) => {
  const {
    data: { isAuthInitialized, tokens },
  } = useContext(AuthContext);

  const [state, setState] = useState<IFeaturesState>(DEFAULT_STATE);

  const [getFeatures] = useLazyQuery<IGetFeaturesResponse>(GET_FEATURES, {
    fetchPolicy: 'network-only',
    pollInterval: 180000,
    onCompleted: (response) => {
      if (response?.featureFlags) {
        const {
          modules: features = [],
          subscription = null,
          expires_at,
          host_count,
          shortcuts_sync_enabled,
          it_adviser_support,
          company_owner,
          verification,
        } = response?.featureFlags;
        setState((prevState) => ({
          ...prevState,
          subscription,
          host_count,
          expiresAt: expires_at,
          features,
          it_adviser_support,
          shortcuts_sync_enabled,
          company_owner,
          verification,
        }));
      }
    },
  });

  useEffect(() => {
    if (!tokens) setState(DEFAULT_STATE);
  }, [tokens]);

  useEffect(() => {
    if (state.isFeaturesInitialized) {
      return;
    }

    if (isAuthInitialized && tokens?.access_token) {
      getFeatures().finally(() => {
        setState((prevState) => ({
          ...prevState,
          isFeaturesInitialized: true,
        }));
      });
    } else {
      setState(DEFAULT_STATE);
    }
  }, [isAuthInitialized, tokens?.access_token]);

  const loadFlags = useCallback((token?: string) => {
    if (!token) return Promise.reject();

    return getFeatures({
      context: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
  }, []);

  const clearFeatures = useCallback(() => {
    setState(DEFAULT_STATE);
  }, []);

  return (
    <FeaturesContext.Provider value={{ ...state, loadFlags, clearFeatures }}>
      {children}
    </FeaturesContext.Provider>
  );
};
